import { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { PageHeading } from '../../layouts/PageHeading';
import { StatusBadge } from '../../components/StatusBadge';
import { AttributesGrid } from '../../components/editors/AttributesGrid';
import { TtsPlayer } from '../../components/tts/TtsPlayer';
import {
  type Attribute,
  useActiveItemSchema,
  useActivateItemSchema,
  useCreateItemSchema,
  useItemSchema,
} from '../../api/itemSchemas';

function newRow(): Attribute {
  return { name: '', type: 'text', required: false, voice_role: 'none', voice_speak_template: null };
}

export function ItemSchemaEditor() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const tenantId = search.get('tenant') ? Number(search.get('tenant')) : null;
  const warehouseId = search.get('warehouse') ? Number(search.get('warehouse')) : null;
  const isNew = id === undefined;
  const readOnly = !isNew;

  const existing = useItemSchema(isNew ? null : Number(id), token);
  const active = useActiveItemSchema(isNew ? warehouseId : null, token);

  const [attributes, setAttributes] = useState<Attribute[]>([newRow()]);
  const create = useCreateItemSchema(token);
  const activate = useActivateItemSchema(token);

  useEffect(() => {
    if (isNew && active.data && active.data.schema?.attributes) setAttributes(active.data.schema.attributes);
    if (!isNew && existing.data) setAttributes(existing.data.schema.attributes);
  }, [isNew, active.data, existing.data]);

  const names = attributes.map((a) => a.name);
  const dup = new Set(names).size !== names.length;
  const hasName = attributes.some((a) => a.voice_role === 'name' && a.name.trim() !== '');
  const allValid = attributes.length > 0 && !dup && hasName && attributes.every((a) => a.name.trim() !== '');

  const save = async (thenActivate: boolean) => {
    if (warehouseId === null) return;
    try {
      const created = await create.mutateAsync({ warehouse_id: warehouseId, schema: { attributes } });
      if (thenActivate) await activate.mutateAsync(created.id);
      navigate(`/admin/item-schemas?tenant=${tenantId}&warehouse=${warehouseId}`);
    } catch (e) {
      alert('Errore: ' + String(e));
    }
  };

  return (
    <div>
      <PageHeading
        scope={isNew ? 'Nuova versione' : `Schema v${existing.data?.version ?? '?'}`}
        target={isNew ? '' : `#${id}`}
        badge={!isNew && existing.data?.is_active ? <StatusBadge tone="success">attiva</StatusBadge> : null}
        actions={
          <Button type="button" color="light" size="sm" onClick={() => navigate(-1)}>↩ Annulla</Button>
        }
      />
      <h3 style={{ fontSize: 14, marginTop: 24 }}>Attributi</h3>
      <AttributesGrid value={attributes} onChange={setAttributes} readOnly={readOnly} />

      <h3 style={{ fontSize: 14, marginTop: 24 }}>Preview dialog</h3>
      <div style={{ background: 'var(--vp-surface-2)', padding: 12, borderRadius: 'var(--vp-r-md)', fontSize: 13 }}>
        {attributes.filter((a) => a.voice_role === 'name').map((a) => (
          <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TtsPlayer text={(a.voice_speak_template ?? '{' + a.name + '}').replace(/\{[a-z_]+\}/gi, '<placeholder>')} />
            <code>{a.voice_speak_template ?? '{' + a.name + '}'}</code>
          </div>
        ))}
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--vp-text-3)' }}>
          La locazione e la quantità sono campi del task — configurali nei prompt SAY_LOCATION / SAY_QUANTITY del dialog template.
        </div>
      </div>

      {isNew ? (
        <div
          style={{
            position: 'sticky', bottom: 0, marginTop: 32, padding: 12, zIndex: 10,
            background: 'var(--vp-surface)', borderTop: '1px solid var(--vp-border)',
            display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
          }}
        >
          <span style={{ marginRight: 'auto', fontSize: 11, color: 'var(--vp-text-3)' }}>
            {dup && 'Nomi duplicati. '}
            {!hasName && attributes.length > 0 && 'Manca un attributo con voice_role=name. '}
            {allValid && 'Pronto per salvare.'}
          </span>
          <Button type="button" color="secondary" outline size="sm" disabled={!allValid || create.isPending} onClick={() => save(false)}>
            Salva
          </Button>
          <Button type="button" color="primary" size="sm" disabled={!allValid || create.isPending} onClick={() => save(true)}>
            Salva e attiva →
          </Button>
        </div>
      ) : existing.data && !existing.data.is_active ? (
        <div
          style={{
            position: 'sticky', bottom: 0, marginTop: 32, padding: 12, zIndex: 10,
            background: 'var(--vp-surface)', borderTop: '1px solid var(--vp-border)',
            display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
          }}
        >
          <span style={{ marginRight: 'auto', fontSize: 11, color: 'var(--vp-text-3)' }}>
            Versione storica · sola lettura. Attiva per sostituire la versione attualmente attiva.
          </span>
          <Button
            type="button"
            color="primary"
            size="sm"
            disabled={activate.isPending}
            onClick={async () => {
              try {
                await activate.mutateAsync(Number(id));
                navigate(`/admin/item-schemas?tenant=${tenantId}&warehouse=${warehouseId}`);
              } catch (e) {
                alert('Errore: ' + String(e));
              }
            }}
          >
            Attiva questa versione →
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default ItemSchemaEditor;
